import { AxiosProgressEvent } from 'axios';

import API from '../../api';
import { ServiceRequestSocketAPI } from '../../api/ServiceRequestSocketAPI';
import TQConnectServiceRequestAPI from '../../api/TQConnectServiceRequestAPI';
import showAppError from '../../shared/error';
import { AcceptedDocFileType } from '../../shared/file';
import { checkIfUserIsHostCreator } from '../../shared/utils';
import { AllowedFileTypeInQuestion } from '../../types/assignmentTypes';
import { MessagesListType } from '../../types/communityMessagesTypes';
import { IFileUploadAsset } from '../../types/communityTypes';
import { EUploadModuleType, IFileAttachment } from '../../types/fileTypes';
import { IServiceRequestMessage } from '../../types/tqConnectTypes/service-requests';
import { AppThunk } from '../store';
import {
  updateSelectedServiceRequestMessageRoom,
  updateServiceRequestChatRoom,
} from '../tqConnectServiceRequestReducer';

/**
 * Uploads files in a service request
 * @param files - files to upload
 * @param loadingCallback - callback to set loading state
 * @param onUploadProgress - callback to get upload progress
 * @returns uploaded files
 */
const uploadFilesInServiceRequest =
  (
    files: File[],
    loadingCallback?: (loading: boolean) => void,
    onUploadProgress?: (e: AxiosProgressEvent) => void,
    uploadKey?: EUploadModuleType,
  ): AppThunk<Promise<IFileAttachment[] | undefined>> =>
  async () => {
    loadingCallback?.(true);
    try {
      const formData = new FormData();

      files.forEach((file) => formData.append('file', file));

      let resp: any = null;

      if (!!uploadKey) {
        resp = await API.uploadFileInAssetsV2(
          files,
          uploadKey!,
          '',
          onUploadProgress,
        );
      } else {
        resp =
          await TQConnectServiceRequestAPI.uploadAttachmentInServiceRequest(
            files,
            onUploadProgress,
          );
      }

      if ([200, 201].includes(resp?.data?.code)) {
        if (!!uploadKey) {
          return resp?.data?.result?.assets?.map((asset: IFileUploadAsset) => ({
            url: asset.url,
            name: asset.fileName,
            type: asset.fileType as AllowedFileTypeInQuestion,
            size: asset.fileSize,
            mimeType: asset.contentType as AcceptedDocFileType,
            key: new URL(asset.url).pathname?.replace('/', ''),
          }));
        } else {
          return resp?.data?.result;
        }
      }
    } catch (error) {
      showAppError(error);
      throw error;
    } finally {
      loadingCallback?.(false);
    }
  };

const MESSAGES_LIMIT = 50;

export const fetchMoreMessages =
  (
    serviceRequestId: string,
    roomId: string,
    // up = scroll to top, down = scroll to bottom
    direction: 'up' | 'down' = 'up',
    callback?: () => void,
  ): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const { serviceRequestChatRoom } = getState().tqConnectServiceRequest;

    if (serviceRequestChatRoom.roomId === roomId) {
      if (serviceRequestChatRoom.allMessagesLoaded && direction === 'up') {
        // console.log('all messages are already loaded');
        return;
      }

      dispatch(
        updateServiceRequestChatRoom({
          ...serviceRequestChatRoom,
          loading: true,
        }),
      );

      const lastMessageId =
        (direction === 'down'
          ? serviceRequestChatRoom.messages[
              serviceRequestChatRoom.messages.length - 1
            ]?._id // Fetch new messages after the last one
          : serviceRequestChatRoom.messages[0]?._id) || null; // Fetch older messages before the first one

      // Determine listType correctly
      const listType: MessagesListType =
        // (chatRoom.messages.length === 0 && 'around-last-seen') || // If no messages exist, fetch the around last seen messages
        (serviceRequestChatRoom.messages.length === 0 && 'latest') || // If no messages exist, fetch the latest messages
        (direction === 'down' && 'after-message') || // Load newer messages
        'before-message'; // Load older messages

      const resp =
        await TQConnectServiceRequestAPI.getMessagesInServiceRequestChatRoom(
          serviceRequestId,
          roomId,
          lastMessageId || null,
          {
            listType,
            limit: MESSAGES_LIMIT,
          },
        );

      const newMessages = resp?.data?.result;

      // Correctly appending messages based on direction
      if (direction === 'up') {
        dispatch(
          updateServiceRequestChatRoom({
            messages: [
              ...newMessages,
              ...(serviceRequestChatRoom.roomId === roomId
                ? serviceRequestChatRoom.messages
                : []),
            ], // Append at top
            roomId,
            allMessagesLoaded: newMessages.length < MESSAGES_LIMIT,
            unreadCount: serviceRequestChatRoom.unreadCount,
            loading: false,
            hasMore: {
              ...serviceRequestChatRoom.hasMore,
              previous: newMessages.length === MESSAGES_LIMIT,
            },
          }),
        );
      } else {
        dispatch(
          updateServiceRequestChatRoom({
            messages: [
              ...(serviceRequestChatRoom.roomId === roomId
                ? serviceRequestChatRoom.messages
                : []),
              ...newMessages,
            ], // Append at bottom
            roomId,
            allMessagesLoaded: serviceRequestChatRoom.allMessagesLoaded,
            unreadCount: serviceRequestChatRoom.unreadCount,
            loading: false,
            hasMore: {
              ...serviceRequestChatRoom.hasMore,
              next: newMessages.length === MESSAGES_LIMIT,
            },
          }),
        );
      }
    }

    // const scrollElement = document.getElementById('bidirectional-scroll');
    const scrollElement = document.querySelector(
      '.message-room__chats__inner__scroll',
    );

    let scrollPosition = 0;
    let oldScroll = 0;

    if (scrollElement) {
      scrollPosition = scrollElement.scrollTop;
      oldScroll = scrollElement.scrollHeight - scrollElement.clientHeight;
    }

    if (direction === 'up' && scrollElement) {
      requestAnimationFrame(() => {
        const newScroll =
          scrollElement.scrollHeight - scrollElement.clientHeight;
        scrollElement.scrollTop = scrollPosition + (newScroll - oldScroll);
      });
    }

    callback?.();
  };

export const fetchMessagesAroundGivenMessage =
  (
    serviceRequestId: string,
    roomId: string,
    messageId: string,
    callback?: () => void,
  ): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const { serviceRequestChatRoom } = getState().tqConnectServiceRequest;

    dispatch(
      updateServiceRequestChatRoom({
        ...serviceRequestChatRoom,
        loading: true,
      }),
    );

    try {
      const resp =
        await TQConnectServiceRequestAPI.getMessagesInServiceRequestChatRoom(
          serviceRequestId,
          roomId,
          messageId,
          {
            listType: 'around-message',
            limit: MESSAGES_LIMIT,
          },
        );

      dispatch(
        updateServiceRequestChatRoom({
          allMessagesLoaded: false,
          messages: resp?.data?.result,
          roomId,
          loading: false,
          hasMore: {
            previous: true,
            next: true,
          },
        }),
      );

      callback?.();

      // setTimeout(() => dispatch(setShowScrollToBottom(true)), 500);
    } catch (err) {
      console.log('err', err);
    }
  };

export const markMessagesAsRead =
  (roomId: string, messageId?: string): AppThunk =>
  (dispatch) => {
    if (messageId) {
      // mark seen the latest message in the room
      ServiceRequestSocketAPI.emit('room.message.see', {
        room: roomId,
        messageId,
      });
    }

    dispatch(
      updateServiceRequestChatRoom({
        roomId,
        unreadCount: 0,
      }),
    );
  };

export const addOrEditMessage =
  (
    newMessage: IServiceRequestMessage,
    type: 'new' | 'edit' = 'new', // new or edit
  ): AppThunk =>
  (dispatch, getState) => {
    const { id: userId, type: userType } = getState().user;
    const {
      serviceRequestChatRoom,
      showScrollToBottom,
      selectedServiceRequestMessageRoom,
    } = getState().tqConnectServiceRequest;

    if (!serviceRequestChatRoom.roomId) return;

    const isUserMessageSender =
      userType === 'creator_restricted'
        ? checkIfUserIsHostCreator(newMessage?.sender?._id)
        : newMessage?.sender?._id === userId;

    const isMessageInChatRoom =
      newMessage.room === serviceRequestChatRoom.roomId;
    const existingMessage =
      serviceRequestChatRoom.messages.find((m) => m._id === newMessage._id) ||
      type === 'edit';

    // const scrollElement = document.getElementById('bidirectional-scroll');
    const scrollElement = document.querySelector(
      isMessageInChatRoom
        ? '.message-room__chats__inner__scroll'
        : '.message-replies-thread',
    );

    let scrollPosition = 0;
    let oldScroll = 0;

    if (scrollElement) {
      scrollPosition = scrollElement.scrollTop;
      oldScroll = scrollElement.scrollHeight - scrollElement.clientHeight;
    }

    // don't do anything if the message neither exists in the chat room or in the replies thread currently but it's an edited message
    // if (type === 'edit' && !existingMessage && !existingReply) {
    //   return;
    // }

    if (isMessageInChatRoom) {
      // console.log('adding message to chat room');
      dispatch(
        updateSelectedServiceRequestMessageRoom({
          ...selectedServiceRequestMessageRoom,
          lastMessageTime: newMessage.createdAt,
        }),
      );
      dispatch(
        updateServiceRequestChatRoom({
          ...serviceRequestChatRoom,
          messages: existingMessage
            ? serviceRequestChatRoom.messages.map((m) =>
                m._id === newMessage._id
                  ? {
                      ...m,
                      ...newMessage,
                    }
                  : m,
              )
            : [...serviceRequestChatRoom.messages, newMessage],
          ...(!isUserMessageSender && {
            newMessagesCount: existingMessage
              ? serviceRequestChatRoom?.newMessagesCount || 0
              : (serviceRequestChatRoom?.newMessagesCount || 0) + 1,
          }),
          unreadCount: existingMessage
            ? serviceRequestChatRoom.unreadCount
            : serviceRequestChatRoom.unreadCount + 1,
        }),
      );

      if (!showScrollToBottom && scrollElement) {
        setTimeout(() => {
          const newScroll =
            scrollElement.scrollHeight - scrollElement.clientHeight;
          scrollElement.scrollTop = scrollPosition + (newScroll - oldScroll);
        }, 50);
      }
    }

    // mark the new message as read if the message is in the chat room and a new message is received (not by the current user)
    if (
      newMessage.room === serviceRequestChatRoom.roomId &&
      type !== 'edit' &&
      !isUserMessageSender &&
      !existingMessage
    ) {
      dispatch(
        markMessagesAsRead(serviceRequestChatRoom.roomId, newMessage._id),
      );
    }
  };

export const updateMessage =
  (newMessage: IServiceRequestMessage): AppThunk =>
  (dispatch) => {
    const { ...message } = newMessage;

    dispatch(addOrEditMessage(message, 'edit'));
  };

export { uploadFilesInServiceRequest };
